"use client";

import { Zap, BarChart3, RefreshCw, Bookmark, ClipboardList, Search, LucideIcon } from "lucide-react";
import { AnimateOnScroll, StaggeredList } from "./index";

const features: { icon: LucideIcon; tag: string; title: string; description: string }[] = [
  {
    icon: ClipboardList,
    tag: "Clarity",
    title: "Spec-Driven Development",
    description:
      "Make intent explicit. Catch vague requirements early through human alignment - no wasted effort on assumptions.",
  },
  {
    icon: BarChart3,
    tag: "Control",
    title: "Human Dashboards",
    description:
      "Track, review, and steer AI collaboration with full visibility into every decision point.",
  },
  {
    icon: Bookmark,
    tag: "Traceability",
    title: "Spec Deltas & Checkpoints",
    description:
      "Every change tracked with intent. Checkpoints create alignment points for branching and exploration.",
  },
  {
    icon: RefreshCw,
    tag: "Memory",
    title: "Session Indexing",
    description:
      "Every AI session indexed for context and learning. Repeatable, reliable collaboration across your org.",
  },
  {
    icon: Zap,
    tag: "CLI",
    title: "One Command Bootstrap",
    description:
      "Initialize Human-AI coordination instantly. Keep projects optimized for maximum LLM leverage.",
  },
  {
    icon: Search,
    tag: "Scale",
    title: "Multi-Repo Native",
    description:
      "Build features across repositories. Designed for large-scale, multi-project development.",
  },
];

export function Features() {
  return (
    <section id="features" className="py-12 sm:py-16 md:py-20 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimateOnScroll variant="fade-up" className="text-center max-w-[640px] mx-auto mb-8 sm:mb-12 md:mb-16">
          <span className="inline-block text-[10px] sm:text-xs font-semibold text-primary uppercase tracking-[1.5px] mb-3 sm:mb-4">
            Platform Features
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mb-3 sm:mb-4 text-text">
            Everything you need for aligned AI development
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-text-muted leading-relaxed">
            A complete platform for managing specifications, collaboration, and
            AI-assisted execution.
          </p>
        </AnimateOnScroll>

        {/* Features Grid */}
        <StaggeredList
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6"
          staggerDelay={80}
          duration={500}
        >
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white border border-border rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-7 transition-all duration-300 hover:border-primary hover:shadow-xl hover:-translate-y-1"
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-primary/10 rounded-lg sm:rounded-xl flex items-center justify-center text-primary">
                  <feature.icon className="w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6 md:h-6" />
                </div>
                <span className="text-[10px] sm:text-[11px] font-semibold text-text-muted bg-bg-alt px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-md uppercase tracking-[0.5px]">
                  {feature.tag}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-text mb-2 sm:mb-2.5">
                {feature.title}
              </h3>
              <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </StaggeredList>
      </div>
    </section>
  );
}
